/**
 * The structure dialog (platform design §5.2.1; owner rulings `325-11801-01/-02/-03`,
 * 2026-09-23; Figma `325:11801` with its three reorder handles deleted, `678:20157`'s three
 * states): opened from the "Estrutura" button in the Equipes header. A level's position never
 * changes -- it is renamed in place, added at the end, or removed -- and nothing reaches the
 * server until `Salvar estrutura`; `Cancelar` drops every staged edit with no confirm.
 *
 * A removal is destructive, so as soon as one is staged the whole pending set is sent to
 * `?preview=true` and the consequence line above the footer shows the server's own counts --
 * this replaces the nested confirm the route version used. Renaming or adding alone never
 * calls it: neither changes what a save would remove.
 *
 * `open`/`onOpenChange` are controlled only when a caller passes them; the header's own
 * button owns its state uncontrolled, the same split `NewTeam` makes.
 *
 * Mounts no `Toaster` of its own: `sonner`'s `Toaster` subscribes to a module-level
 * singleton with no `id` scoping, and `../routes.ts` mounts two of this component at
 * once (the header's and the empty state's), so the route that composes them owns the
 * one `Toaster` both share.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { toast } from 'sonner';

import { getLocale } from '../../../i18n';
import { integer } from '../../../i18n/format';
import { DialogSurfaceFooter } from '../../../chrome/form/FormDialog';
import { ft } from '../../../chrome/form/messages';
import { ErrorState } from '../../../chrome/state';
import { Button } from '../../../ui/button';
import { Chip } from '../../../ui/chip';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../../ui/dialog';
import { Skeleton } from '../../../ui/skeleton';
import { useCan } from '../../session/orgContext';
import { plural, t } from '../i18n';
import { Structure, type DraftLevel } from './Structure';
import { usePreviewStructure, useSaveStructure, useStructure } from './hooks';
import type { LevelBatchEntryIn, LevelOut, LevelRemovalOut } from './schemas';

function draftFromLevels(levels: LevelOut[]): DraftLevel[] {
  return [...levels]
    .sort((a, b) => a.position - b.position)
    .map((level) => ({
      kind: 'existing',
      id: level.id,
      name: level.name,
      teams: level.teams,
      removed: false,
    }));
}

function payloadOf(rows: DraftLevel[]): LevelBatchEntryIn[] {
  return rows
    .filter((row) => row.kind === 'new' || !row.removed)
    .map((row) =>
      row.kind === 'existing' ? { id: row.id, name: row.name } : { id: null, name: row.name },
    );
}

function ConsequenceLine(props: { preview: LevelRemovalOut }): ReactNode {
  const { preview } = props;
  const locale = getLocale();
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-body-sm">
      <span className="text-muted-foreground">{t('teams.editor.consequence.label')}</span>
      {preview.teams_removed > 0 && (
        <Chip size="sm">
          {plural('teams.editor.consequence.teamsRemoved.other', preview.teams_removed, {
            count: integer(preview.teams_removed, locale),
          })}
        </Chip>
      )}
      {preview.accounts_moved > 0 && (
        <Chip size="sm">
          {plural('teams.editor.consequence.accountsMoved.other', preview.accounts_moved, {
            count: integer(preview.accounts_moved, locale),
          })}
        </Chip>
      )}
      {/* Only removing the top level (position 1) can leave an account with no level above
       *  it to move up to -- `accounts_unassigned` is 0 for every other staged removal, so
       *  gating on the count (never on "which level") keeps this from ever reading "0
       *  contas ficam sem equipe". */}
      {preview.accounts_unassigned > 0 && (
        <Chip size="sm" tone="negative">
          {plural(
            'teams.editor.consequence.accountsUnassigned.other',
            preview.accounts_unassigned,
            { count: integer(preview.accounts_unassigned, locale) },
          )}
        </Chip>
      )}
    </div>
  );
}

export interface StructureDialogProps {
  campaignId: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function StructureDialog(props: StructureDialogProps): ReactNode {
  const { campaignId } = props;
  const canWrite = useCan('control_plane.write');
  const structureQuery = useStructure(campaignId);
  const saveMutation = useSaveStructure(campaignId);
  // `pending` reaches the save button a notify tick and a render after the click; this closes
  // that gap against a double click.
  const saving = useRef(false);
  const previewMutation = usePreviewStructure(campaignId);

  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = props.open !== undefined;
  const open = isControlled ? (props.open ?? false) : internalOpen;
  const setOpen = isControlled ? (props.onOpenChange ?? (() => {})) : setInternalOpen;

  const [rows, setRows] = useState<DraftLevel[]>([]);
  const [newKeySeq, setNewKeySeq] = useState(0);
  const [lastAddedKey, setLastAddedKey] = useState<string | null>(null);
  const [hasStagedRemoval, setHasStagedRemoval] = useState(false);

  // The preview call carries the removed set, not the whole draft -- a rename or an add
  // never changes what the server would remove, so re-deriving this on every keystroke
  // would ask it again for an answer it already gave. Compared as a joined string so the
  // set is order-independent.
  const previewedRemovalsRef = useRef('');

  // Re-derived every time the dialog opens (and once the structure it seeds from has loaded),
  // never carried across a previous open -- the same reason `NewTeam` and `EditTeam` reset on
  // open rather than tracking `structureQuery.data` afterwards.
  useEffect(() => {
    if (!open) return;
    setRows(draftFromLevels(structureQuery.data?.levels ?? []));
    setLastAddedKey(null);
    setHasStagedRemoval(false);
    previewedRemovalsRef.current = '';
    saveMutation.reset();
    previewMutation.reset();
  }, [open, structureQuery.data]);

  function applyRows(next: DraftLevel[]): void {
    setRows(next);
    const removedIds = next
      .filter(
        (row): row is Extract<DraftLevel, { kind: 'existing' }> =>
          row.kind === 'existing' && row.removed,
      )
      .map((row) => row.id)
      .sort()
      .join(',');
    const removalStaged = removedIds.length > 0;
    setHasStagedRemoval(removalStaged);
    if (removalStaged && removedIds !== previewedRemovalsRef.current) {
      previewedRemovalsRef.current = removedIds;
      previewMutation.mutate({ levels: payloadOf(next) });
    }
  }

  function handleRename(index: number, name: string): void {
    applyRows(rows.map((row, i) => (i === index ? { ...row, name } : row)));
  }

  function handleToggleRemove(index: number): void {
    applyRows(
      rows.map((row, i) =>
        i === index && row.kind === 'existing' ? { ...row, removed: !row.removed } : row,
      ),
    );
  }

  function handleDiscardNew(index: number): void {
    applyRows(rows.filter((_, i) => i !== index));
  }

  function handleAddLevel(): void {
    const key = `new-${newKeySeq}`;
    setNewKeySeq((seq) => seq + 1);
    setLastAddedKey(key);
    applyRows([...rows, { kind: 'new', key, name: '' }]);
  }

  function handleSave(): void {
    if (saving.current) return;
    saving.current = true;
    void saveMutation
      .mutateAsync({ levels: payloadOf(rows) })
      .then(
        () => {
          setOpen(false);
          toast.success(t('teams.editor.saved'));
        },
        // `saveFailed` below renders the rejection; the dialog stays open on it.
        () => undefined,
      )
      .finally(() => {
        saving.current = false;
      });
  }

  if (!canWrite) return null;

  const pending = saveMutation.isPending;
  const saveFailed = saveMutation.isError;
  const preview = hasStagedRemoval ? previewMutation.data : undefined;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {isControlled ? undefined : (
        <DialogTrigger asChild>
          <Button type="button" variant="outline">
            {t('teams.editor.trigger')}
          </Button>
        </DialogTrigger>
      )}
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('teams.editor.title')}</DialogTitle>
        </DialogHeader>

        {structureQuery.status === 'pending' && (
          <div className="flex flex-col gap-2" role="status" aria-live="polite">
            <span className="sr-only">{t('teams.loading')}</span>
            {Array.from({ length: 3 }, (_, index) => (
              <Skeleton key={index} aria-hidden className="h-9 w-full" />
            ))}
          </div>
        )}
        {structureQuery.status === 'error' && (
          <ErrorState
            failure={structureQuery.error.failure}
            onRetry={() => void structureQuery.refetch()}
          />
        )}
        {structureQuery.status === 'success' && (
          <Structure
            rows={rows}
            disabled={pending}
            lastAddedKey={lastAddedKey}
            onRename={handleRename}
            onToggleRemove={handleToggleRemove}
            onDiscardNew={handleDiscardNew}
            onAddLevel={handleAddLevel}
          />
        )}

        {saveFailed && (
          <ErrorState
            failure={{ kind: 'invalid', field: null, message: t('teams.editor.save.failed') }}
          />
        )}
        {preview !== undefined && <ConsequenceLine preview={preview} />}

        <DialogSurfaceFooter>
          <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={pending}>
            {ft('cancel')}
          </Button>
          <Button type="button" onClick={handleSave} disabled={pending}>
            {pending ? t('teams.editor.saving') : t('teams.editor.save')}
          </Button>
        </DialogSurfaceFooter>
      </DialogContent>
    </Dialog>
  );
}
